import { useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { Layout } from '@/components/Layout';
import { LoginPage } from '@/pages/LoginPage';
import { ForcePasswordChangePage } from '@/pages/ForcePasswordChangePage';
import { DashboardPage } from '@/pages/DashboardPage';
import { TxnLogPage } from '@/pages/TxnLogPage';
import { ClientsPage } from '@/pages/ClientsPage';
import { ExportReportPage } from '@/pages/ExportReportPage';
import { CardsPage } from '@/pages/CardsPage';
import { RecentDebitsPage } from '@/pages/RecentDebitsPage';
import { DebitsLedgerPage } from '@/pages/DebitsLedgerPage';
import { HoldsPage } from '@/pages/HoldsPage';
import { CancelQueuePage } from '@/pages/CancelQueuePage';
import { SmsTemplatesPage } from '@/pages/SmsTemplatesPage';
import { PayRequestsPage } from '@/pages/PayRequestsPage';
import { SmsPatternsPage } from '@/pages/SmsPatternsPage';
import { OtpAlertsPage } from '@/pages/OtpAlertsPage';
import { ApprovalsPage } from '@/pages/ApprovalsPage';
import { SessionsPage } from '@/pages/SessionsPage';
import type { SseStatus } from '@/hooks/useSse';

const qc = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 5_000, refetchOnWindowFocus: false, retry: 1 },
  },
});

function ProtectedRoutes() {
  const { user } = useAuth();
  const [sseStatus, setSseStatus] = useState<SseStatus>('idle');
  if (!user) return <Navigate to="/login" replace />;
  // Gate the whole app behind a mandatory password change until the
  // user has set their own private password.
  if (user.mustChangePassword) return <ForcePasswordChangePage />;
  return (
    <Routes>
      <Route element={<Layout sseStatus={sseStatus} />}>
        <Route index element={<DashboardPage onSseStatus={setSseStatus} />} />
        <Route path="transactions" element={<TxnLogPage onSseStatus={setSseStatus} />} />
        <Route path="parking-lot" element={<Navigate to="/transactions" replace />} />
        <Route path="captures" element={<Navigate to="/transactions" replace />} />
        <Route path="otp-log" element={<Navigate to="/transactions" replace />} />
        <Route path="clients" element={<ClientsPage />} />
        <Route path="recent-debits" element={<RecentDebitsPage />} />
        <Route path="debits" element={<DebitsLedgerPage />} />
        <Route path="export" element={<ExportReportPage />} />
        <Route path="cards" element={<CardsPage />} />
        <Route path="otp-alerts" element={<OtpAlertsPage />} />
        <Route path="approvals" element={<ApprovalsPage />} />
        <Route path="sessions" element={<SessionsPage />} />
        <Route path="transfers" element={<Navigate to="/" replace />} />
        <Route path="wallets" element={<Navigate to="/" replace />} />
        <Route path="reports" element={<Navigate to="/" replace />} />
        <Route path="reconcile" element={<Navigate to="/recent-debits" replace />} />
        <Route path="holds" element={<HoldsPage />} />
        <Route path="cancel-queue" element={<CancelQueuePage />} />
        <Route path="sms-templates" element={<SmsTemplatesPage />} />
        <Route path="pay-requests" element={<PayRequestsPage />} />
        <Route path="patterns" element={<SmsPatternsPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}

function AppShell() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/*" element={<ProtectedRoutes />} />
      </Routes>
    </BrowserRouter>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={qc}>
      <AuthProvider>
        <AppShell />
      </AuthProvider>
    </QueryClientProvider>
  );
}
